import { CopyOutlined, DeleteOutlined, FormOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Input, Modal, Select, Switch, Table } from 'antd'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AdvertiserBean, CreativeBean, ItemResponseCreativeBeanObject, ListPagination } from 'src/api/api'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import { StorageKey, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { useDebounce } from 'src/util/useDebounce'
import { toSort, useSorter } from 'src/util/useSorter'
import creativeApi from './CreativeApi'
import { enumView } from 'src/util/Util'

const Column = Table.Column
const Option = Select.Option

interface Props {
    areActionsVertical?: boolean
}

const storageWriter = storageWriterFactory('creative')
const storageReader = storageReaderFactory('creative')

const formInfoItems: FormDataType[] = [
    FormDataType.Advertisers
]

const CreativeList: React.FC<Props> = ({ areActionsVertical }) => {

    const [data, setData] = useState<CreativeBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [info, setInfo] = useState({
        advertisers: []
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const [advertiser, setAdvertiser] = useState<number>()
    const [active, setActive] = useState<boolean>()
    const [error, setError] = useState()

    const loadData = async (page: number) => {
        const response = await creativeApi.list(pagination.defaultPageSize, page, toSort(sorter), search, advertiser, active)

        const array = response.data as Array<ItemResponseCreativeBeanObject>
        const creativeBeans: CreativeBean[] = array.map(item => item.data as CreativeBean)

        if (creativeBeans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage
            })

            setData(creativeBeans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    const loadInfo = async () => {
        const resp = await formDataLoader(...formInfoItems)
        setInfo(resp as any)
    }

    useEffect(() => {
        loadInfo()
    }, [])

    useEffect(() => {
        const searchParams = storageReader.read(StorageKey.SearchParams)
        const page = searchParams.page || pagination.current

        loadData(page)
    }, [debouncedSearch, advertiser, active, sorter])

    const activate = async (record: CreativeBean, index) => {
        await creativeApi.activate(record.id)

        const bean = data[index]
        bean.active = !record.active

        setData([...data])
    }

    const remove = (record: CreativeBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                try {
                    await creativeApi.delete(record.id as number)

                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }

                    loadData(page)
                } catch (e) {
                    setError(e.error)
                }
            }
        })
    }

    return <Card
        title="Creative"
        className="list-card"
        extra={<div>
            <Link to={`/creative/create`} className="create-new-link">
                <Button htmlType="button" type="primary" icon={<PlusOutlined />}>
                    New Creative
                </Button>
            </Link>
            <Link to={`/creative/bulk`} className="create-bulk-link">
                <Button htmlType="button" type="primary" icon={<PlusOutlined />}>
                    New Bulk
                </Button>
            </Link>
        </div>}
    >
        {error && <Alert message={error} type="error" />}
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name" onChange={e => setSearch(e.target.value || undefined)} />
            <Select placeholder="Filter by Advertiser" onChange={setAdvertiser} allowClear>
                {info.advertisers.map((item: AdvertiserBean) => <Option key={item.id} value={item.id}>{item.name}</Option>)}
            </Select>
            <Select placeholder="Filter by Active" onChange={setActive} allowClear>
                {Object.values(['Active', 'Inactive']).map(type => <Option key={type} value={type == 'Active'}>{type}</Option>)}
            </Select>
        </div>
        <Table
            rowKey="id"
            size="middle"
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column
                sorter
                key="id"
                title="ID"
                dataIndex="id"
            />
            <Column
                sorter
                key="name"
                title="Name"
                dataIndex="name"
                render={(text, record: CreativeBean) => <Link to={`/creative/${record.id}/edit`}>{text}</Link>}
            />
            <Column
                key="advertiserId"
                title="Advertiser"
                dataIndex="advertiserId"
                render={id => (info.advertisers as AdvertiserBean[]).find(item => id == item.id)?.name}
            />
            <Column
                key="format"
                title="Format"
                dataIndex="format"
                render={enumView}
            />
            <Column
                align="center"
                key="active"
                title="Active"
                dataIndex="active"
                render={(text, record: CreativeBean, index) => <Switch checked={record.active} size="small" onChange={e => activate(record, index)} />}
            />
            <Column
                key="modified"
                title="Last Modified"
                dataIndex="modified"
                sorter
            />
            <Column align="center" key="action" title="Actions" render={(text: string, record: CreativeBean) => (
                <span>
                    <Link to={`/creative/${record.id}/edit`}>
                        <Button type="text" >
                            <FormOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Link to={`/creative/create?copyCreativeId=${record.id}`}>
                        <Button type="text" >
                            <CopyOutlined className="actions-btn" />
                        </Button>
                    </Link>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            )} />
        </Table>
    </Card>
}

export default CreativeList


// WEBPACK FOOTER //
// ./src/component/app/creative/CreativeList.tsx